function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Ribbon } from "../../components/analytics-kit";
import { BaseDataViewComponentAsync } from "../../components/base";
import publishSearch from '../../functions/publish-search';
import publishEvent from "../../functions/publish-event";
import ServiceFactory from '../../service';
import { Loader } from '../../components/loaders';
import Error from '../../components/error';
import markupParser from "../../functions/markup-parser";
import Popover from "../../components/popover";
import { Button } from "@progress/kendo-react-buttons";
import { kwService, normDomain, selfRow, saveSelfSite } from "../keywords/common";
import account from "../../data/account";
import Auth from "../../functions/authenticate";

/*
 * Company Analysis report (apps redesign 2026-10-09, board 3).
 * Header (title = company, "domain · analyzed <date>", Export + Run again),
 * a four-tile ribbon from the run's structured data, then two columns:
 * the competitors table (with the one primary action, "Track these in
 * Keywords", which seeds the Keyword Intelligence registry through
 * POST /keywords/seed-from-analysis/{runId}) and the report's sections as
 * short text blocks. Sections that only say nothing was found are hidden
 * and named in one muted line. Export, Run again and Locations are kept.
 *
 * The small markdown renderer and text helpers below are shared with the
 * Market Trends views.
 */

// ---------------------------------------------------------------- helpers

export const fmtInt = n => Math.round(Number(n) || 0).toLocaleString("en-US");
export function readableDate(d) {
  const x = new Date(d);
  if (isNaN(x.getTime())) return "";
  const now = new Date();
  const day = a => new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const diff = Math.round((day(now) - day(x)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  const opts = {
    month: "short",
    day: "numeric"
  };
  if (x.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return x.toLocaleDateString("en-US", opts);
}
export const hostOf = u => {
  try {
    return new URL(/^https?:/.test(u) ? u : "https://" + u).hostname.replace(/^www\./, "");
  } catch (e) {
    return String(u || "").replace(/^https?:\/\/(www\.)?/, "").replace(/\/.*$/, "");
  }
};

// Text that only says a tool came back empty.
export const NOTHING_FOUND = /no (specific )?(\w+ )?(data|details|information|coverage|news)|not available|appears to be no/i;
// Sites that list every business (maps, directories, social networks,
// reference sites) share search results with everyone, so they aren't
// competitors. Left out of the competitor table, the count and Keywords.
const DIRECTORY_SITES = ["mapquest.com", "wheree.com", "yelp.com", "yellowpages.com", "superpages.com", "whitepages.com", "manta.com", "bbb.org", "foursquare.com", "citysearch.com", "local.com", "hotfrog.com", "merchantcircle.com", "chamberofcommerce.com", "cylex.us.com", "brownbook.net", "showmelocal.com", "find-open.com", "allbiz.com", "bizapedia.com", "zoominfo.com", "dnb.com", "opencorporates.com", "nextdoor.com", "angi.com", "angieslist.com", "thumbtack.com", "tripadvisor.com", "google.com", "bing.com", "apple.com", "waze.com", "facebook.com", "instagram.com", "linkedin.com", "twitter.com", "x.com", "pinterest.com", "youtube.com", "tiktok.com", "reddit.com", "wikipedia.org", "wikidata.org", "indeed.com", "glassdoor.com", "groupon.com"];
export function isDirectorySite(domain) {
  const d = normDomain(domain);
  return !!d && DIRECTORY_SITES.some(x => d === x || d.endsWith("." + x));
}
// Competitors worth showing: the analysis output minus directory sites.
export function realCompetitors(cdr) {
  return (cdr && cdr.CompetitorDomainMetrics || []).filter(c => c && c.Domain && !isDirectorySite(c.Domain));
}
export const saysNothing = body => {
  const t = String(body || "").trim();
  return !t || t.length < 700 && NOTHING_FOUND.test(t);
};

// Split report markdown into [{title, body}] on `#` headings, or on lines
// that are only a bold known section title ("**3. Domain Metrics**").
export function splitSections(text, knownRe) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let cur = null;
  const headOf = line => {
    let m = /^#{1,4}\s*(?:\d+\.?\s*)?(.+?)\s*#*\s*$/.exec(line);
    if (m) {
      const t = m[1].replace(/\*\*/g, "").replace(/:$/, "").trim();
      return !knownRe || knownRe.test(t) ? t : null;
    }
    m = /^\s*(?:\d+\.\s*)?\*\*\s*(?:\d+\.?\s*)?([^*]{2,60}?)\s*:?\s*\*\*\s*:?\s*$/.exec(line);
    if (m && (!knownRe || knownRe.test(m[1]))) return m[1].trim();
    return null;
  };
  lines.forEach(line => {
    const h = headOf(line);
    if (h) {
      cur = {
        title: h,
        body: []
      };
      out.push(cur);
      return;
    }
    if (!cur) {
      cur = {
        title: "",
        body: []
      };
      out.push(cur);
    }
    cur.body.push(line);
  });
  return out.map(s => ({
    title: s.title,
    body: s.body.join("\n").trim()
  })).filter(s => s.body);
}

// Tidy agent text: drop bare bracketed references that are not links
// ("[Domain Metrics]", "[1]"), stray backticks and horizontal rules.
export function cleanText(md) {
  return String(md || "").replace(/`/g, "").replace(/^\s*(-{3,}|\*{3,}|_{3,})\s*$/gm, "").replace(/\s*\(?\b(see|refer to|as noted in)\b[^.()\n]*\[[^\]\n]+\](?!\()[^.()\n]*[.)]?/gi, "")
  // "[Domain Metrics Analysis](data provided)": a citation to the agent's own
  // tool data, written as a link with no address. Drop it like a bare one.
  .replace(/\s*\[([^\]\n]{1,120})\]\((?!\s*https?:\/\/)[^)\n]{0,120}\)/gi, "").replace(/\s*\[([^\]\n]{1,120})\](?!\()/g, "").replace(/\(\s*\)/g, "").replace(/[ \t]+([.,;:])/g, "$1");
}
const safeUrl = u => /^https?:\/\//i.test(u) ? u : null;
function inline(text, kp) {
  const nodes = [];
  const re = /(\[([^\]\n]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\s][^*]*)\*|(https?:\/\/[^\s)<]+))/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const k = `${kp}-${i++}`;
    if (m[2]) {
      const href = safeUrl(m[3]);
      nodes.push(href ? /*#__PURE__*/React.createElement("a", {
        key: k,
        href: href,
        target: "_blank",
        rel: "noopener noreferrer"
      }, m[2]) : m[2]);
    } else if (m[4] || m[5]) nodes.push(/*#__PURE__*/React.createElement("strong", {
      key: k
    }, m[4] || m[5]));else if (m[6]) nodes.push(/*#__PURE__*/React.createElement("em", {
      key: k
    }, m[6]));else if (m[7]) {
      const url = m[7].replace(/[.,;:]+$/, "");
      nodes.push(/*#__PURE__*/React.createElement("a", {
        key: k,
        href: url,
        target: "_blank",
        rel: "noopener noreferrer"
      }, hostOf(url)));
      if (url.length < m[7].length) nodes.push(m[7].slice(url.length));
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}
export const Inline = ({
  text
}) => /*#__PURE__*/React.createElement(React.Fragment, null, inline(cleanText(text).trim(), "i"));

/** Markdown for agent text: headings, lists, bold, italic, links, bare URLs. */
export function Md({
  source,
  className
}) {
  const lines = cleanText(source).split("\n");
  const blocks = [];
  let i = 0;
  let key = 0;
  const isList = l => /^\s*([-*•]|\d+[.)])\s+/.test(l);
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = /^#{1,6}\s+(.*)$/.exec(line);
    if (h) {
      blocks.push(/*#__PURE__*/React.createElement("h4", {
        key: key++
      }, inline(h[1].replace(/\*\*/g, ""), `h${key}`)));
      i++;
      continue;
    }
    if (isList(line)) {
      const items = [];
      while (i < lines.length && (isList(lines[i]) || lines[i].trim() && /^\s{2,}/.test(lines[i]) && items.length)) {
        const t = lines[i].replace(/^\s*([-*•]|\d+[.)])\s+/, "").trim();
        if (isList(lines[i])) items.push(t);else items[items.length - 1] += " " + t;
        i++;
      }
      blocks.push(/*#__PURE__*/React.createElement("ul", {
        key: key++
      }, items.map((t, j) => /*#__PURE__*/React.createElement("li", {
        key: j
      }, inline(t, `l${key}-${j}`)))));
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^#{1,6}\s+/.test(lines[i]) && !isList(lines[i])) {
      para.push(lines[i].trim());
      i++;
    }
    blocks.push(/*#__PURE__*/React.createElement("p", {
      key: key++
    }, inline(para.join(" "), `p${key}`)));
  }
  if (!blocks.length) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: `apr-md ${className || ""}`
  }, blocks);
}

// Structured blobs inside the run's `data` field; each may be a string
// (double-encoded) or already an object. Every access fails soft.
function blob(inner, key) {
  try {
    const v = inner[key];
    return typeof v === "string" ? JSON.parse(v) : v || null;
  } catch (e) {
    return null;
  }
}
export function parseCompanyData(data) {
  try {
    const inner = typeof data === "string" ? JSON.parse(data) : data || {};
    const cdr = (blob(inner, "businessDomainMetricsAnalysis") || {}).CompetitorDomainResults || null;
    const sentiment = (blob(inner, "brandSentimentAnalysis") || {}).ContentAnalysisSummary || null;
    const locSent = blob(inner, "locationSummaryAndSentimentAnalysis");
    return {
      cdr,
      sentiment,
      locSent
    };
  } catch (e) {
    return {
      cdr: null,
      sentiment: null,
      locSent: null
    };
  }
}

/** Sentiment in words: { word, phrase, pos, neg } or null when there are no mentions. */
export function sentimentWords(s) {
  if (!s) return null;
  const pos = Number(s.Positive) || 0;
  const neg = Number(s.Negative) || 0;
  if (pos + neg === 0) return null;
  const word = pos > neg * 1.2 ? "Positive" : neg > pos * 1.2 ? "Negative" : "Mixed";
  const phrase = word === "Mixed" ? "Mixed" : `Mostly ${word.toLowerCase()}`;
  return {
    word,
    phrase,
    pos,
    neg
  };
}

// Report section titles in plain words.
const SECTION_LABELS = [[/overview/i, "What they do"], [/product|positioning/i, "What they sell"], [/domain|traffic|metric/i, "How visible they are"], [/location/i, "Their locations"], [/brand|sentiment|reputation/i, "What people say"], [/demograph|audience/i, "Who their customers are"], [/news|press/i, "In the news"], [/social/i, "Social media"], [/competit|landscape/i, "Who they compete with"], [/takeaway|strateg|recommend/i, "What this means for you"]];
const KNOWN_COMPANY = /overview|product|positioning|domain|metric|location|brand|sentiment|demograph|news|social|competit|landscape|takeaway|strateg/i;
const plainTitle = t => {
  const hit = SECTION_LABELS.find(([re]) => re.test(t));
  return hit ? hit[1] : t;
};

// ---------------------------------------------------------------- view

class MainComponent extends React.Component {
  constructor(props) {
    super(props);
    // Adds the analyzed site and its competitors to Keywords. The analyzed site
    // becomes "your site" only when it is the workspace's own domain: an
    // analysis of a competitor must never become your site (the server-side
    // seed did that whenever no site was set yet, and every rank and AI
    // mention was then measured on the competitor).
    _defineProperty(this, "seedCompetitors", async () => {
      if (this.state.seeding) return;
      this.setState({
        seeding: true
      });
      try {
        const registry = (await kwService("/competitors").getAsync()) || [];
        const have = new Set(registry.map(r => normDomain(r.domain)));
        const {
          cdr
        } = parseCompanyData(this.props.data.data);
        const subject = normDomain(cdr && cdr.TargetDomain || this.props.data.domain);
        const mine = normDomain((account.get() || {}).domain);
        if (subject && mine && subject === mine && !selfRow(registry)) await saveSelfSite(subject, registry);else if (subject && !have.has(subject)) await kwService("/competitors").postAsync({
          domain: subject,
          label: null,
          isSelf: false
        });
        have.add(subject);
        for (const c of realCompetitors(cdr)) {
          const d = normDomain(c.Domain);
          if (!d || have.has(d) || d === mine) continue;
          await kwService("/competitors").postAsync({
            domain: d,
            label: null,
            isSelf: false
          });
          have.add(d);
        }
        this.setState({
          seeding: false,
          notice: "These sites are now tracked in Keywords. Weekly search snapshots start on the next cycle."
        });
        this.loadCompetitors();
      } catch (e) {
        this.setState({
          seeding: false,
          notice: "Could not add these sites to Keywords. Try again in a moment."
        });
      }
    });
    _defineProperty(this, "openKeywords", () => {
      this.props.history.push("/Keywords");
    });
    // Run the same analysis again (same site, name and locations), then go
    // to the list, which shows the new row as Running and polls it.
    _defineProperty(this, "rerun", () => {
      const data = this.props.data;
      if (this.state.rerunning) return;
      let locations = [];
      try {
        locations = JSON.parse(data.locations) || [];
      } catch (e) {/* none */}
      const auth = Auth.get() || {};
      const Service = ServiceFactory.getType('/company-analysis', 'research');
      this.setState({
        rerunning: true
      });
      new Service().post({
        url: data.domain,
        company: data.company,
        locations,
        userId: auth.email || ''
      }, err => {
        if (!this._alive) return;
        if (err) {
          this.setState({
            rerunning: false,
            notice: "Could not start a new analysis. Try again in a moment."
          });
          return;
        }
        this.props.history.push("/Research/Company");
      });
    });
    _defineProperty(this, "onExportClick", () => {
      let data = this.props.data;
      let content = markupParser(cleanText(data.text));
      let fileName = String(data.company || 'company').replace(/ /g, '_') + '_analysis.doc';
      const header = `
    <html xmlns:o='urn:schemas-microsoft-com:office:office'
          xmlns:w='urn:schemas-microsoft-com:office:word'
          xmlns='http://www.w3.org/TR/REC-html40'>
    <head><meta charset='utf-8'><title>${data.company} Analysis</title></head><body>`;
      const fullHTML = header + content + '</body></html>';
      const blobDoc = new Blob(['﻿', fullHTML], {
        type: 'application/msword'
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blobDoc);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
    this.state = {
      trackedDomains: null,
      registry: null,
      seeding: false,
      notice: null,
      rerunning: false
    };
    this._alive = true;
  }
  componentDidMount() {
    publishSearch('Research');
    publishEvent('reset-search', {});
    this.loadCompetitors();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  loadCompetitors() {
    kwService("/competitors").getAsync().then(rows => {
      if (this._alive) this.setState({
        registry: rows || [],
        trackedDomains: (rows || []).map(r => normDomain(r.domain))
      });
    }).catch(() => {
      if (this._alive) this.setState({
        registry: null,
        trackedDomains: []
      });
    });
  }
  isTracked(domain) {
    return (this.state.trackedDomains || []).includes(normDomain(domain));
  }
  renderCompetitors(cdr) {
    if (!cdr) return null;
    const {
      trackedDomains,
      seeding
    } = this.state;
    const target = cdr.DomainMetrics || null;
    const etv = x => Number(((x || {}).OrganicStats || {}).Etv) || 0;
    const all = cdr.CompetitorDomainMetrics || [];
    const competitors = realCompetitors(cdr).slice().sort((a, b) => etv(b) - etv(a));
    const skipped = all.filter(c => c && c.Domain && isDirectorySite(c.Domain)).map(c => normDomain(c.Domain));
    const mine = normDomain((account.get() || {}).domain);
    const targetIsYou = this.props.data.subjectIsTenant === true || !!mine && normDomain(cdr.TargetDomain) === mine;
    if (!target && !competitors.length) return null;
    const allTracked = [cdr.TargetDomain, ...competitors.map(c => c.Domain)].filter(Boolean).every(d => this.isTracked(d));
    const pos = x => {
      const v = Number(x.AvgPosition);
      return v ? String(Math.round(v)) : "Not ranked";
    };
    return /*#__PURE__*/React.createElement("section", {
      className: "apr-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "apr-card-head"
    }, /*#__PURE__*/React.createElement("b", null, "Competitors in search"), allTracked ? /*#__PURE__*/React.createElement(Button, {
      onClick: this.openKeywords
    }, "Open in Keywords") : /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: seeding || trackedDomains === null,
      onClick: this.seedCompetitors,
      title: "Adds these sites to Keywords for weekly tracking"
    }, seeding ? "Adding…" : "Track these in Keywords")), /*#__PURE__*/React.createElement("div", {
      className: "apr-table-wrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "apr-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Site"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Avg. position"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Monthly visits"))), /*#__PURE__*/React.createElement("tbody", null, target ? /*#__PURE__*/React.createElement("tr", {
      className: "apr-you"
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, cdr.TargetDomain), " ", targetIsYou ? /*#__PURE__*/React.createElement("span", {
      className: "apr-chip apr-chip-you"
    }, "You") : /*#__PURE__*/React.createElement("span", {
      className: "apr-muted"
    }, "analyzed")), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, pos(target)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmtInt(etv(target)))) : null, competitors.map(c => /*#__PURE__*/React.createElement("tr", {
      key: c.Domain
    }, /*#__PURE__*/React.createElement("td", null, c.Domain, this.isTracked(c.Domain) ? /*#__PURE__*/React.createElement("span", {
      className: "apr-muted apr-tracked"
    }, "tracked") : null), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, pos(c)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, fmtInt(etv(c)))))))), /*#__PURE__*/React.createElement("p", {
      className: "apr-foot"
    }, "Average position in organic search results. Monthly visits are estimated from search rankings.", skipped.length ? ` Directory, map and social sites (${skipped.join(", ")}) are left out because they list every business.` : ""));
  }
  render() {
    const data = this.props.data;
    const {
      notice,
      rerunning
    } = this.state;
    const {
      cdr,
      sentiment,
      locSent
    } = parseCompanyData(data.data);
    const target = cdr && cdr.DomainMetrics;
    const sw = sentimentWords(sentiment);
    const rating = locSent ? locSent.AverageRating || locSent.Rating || null : null;
    let arrLocations = [];
    try {
      arrLocations = JSON.parse(data.locations) || [];
    } catch (e) {/* none */}
    const domainHost = hostOf(data.domain || "");
    const created = readableDate(data.createdDate);
    const all = splitSections(data.text || "", KNOWN_COMPANY).map(s => ({
      ...s,
      label: s.title ? plainTitle(s.title) : "Summary"
    }));
    // A leading untitled block that is just the report title adds nothing.
    const sections = all.filter(s => !(s.label === "Summary" && s.body.length < 80));
    const shown = sections.filter(s => !saysNothing(s.body));
    const hidden = sections.filter(s => saysNothing(s.body)).map(s => s.label.toLowerCase());
    const tiles = [target ? {
      l: "Average search position",
      v: Number(target.AvgPosition) ? String(Math.round(Number(target.AvgPosition))) : "Not ranked",
      s: "organic results"
    } : null, target ? {
      l: "Monthly visits from search",
      v: fmtInt((target.OrganicStats || {}).Etv),
      s: "estimated"
    } : null, sw ? {
      l: "How people talk about it",
      v: sw.word,
      s: `${fmtInt(sw.pos)} positive, ${fmtInt(sw.neg)} negative mentions`
    } : null, rating ? {
      l: "Rating",
      v: `${rating} of 5`,
      s: "location reviews"
    } : null, cdr ? {
      l: "Competitors found",
      v: String(realCompetitors(cdr).length)
    } : null].filter(Boolean);
    const competitorsCard = this.renderCompetitors(cdr);
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view apr-root apr-company-report"
    }, /*#__PURE__*/React.createElement("header", {
      className: "apr-hdr"
    }, /*#__PURE__*/React.createElement("div", {
      className: "apr-hdr-text"
    }, /*#__PURE__*/React.createElement("span", {
      className: "apr-crumb"
    }, /*#__PURE__*/React.createElement(Link, {
      to: "/Apps"
    }, "Apps"), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
      to: "/Research/Company"
    }, "Company Analysis")), /*#__PURE__*/React.createElement("h1", null, data.company), /*#__PURE__*/React.createElement("span", {
      className: "apr-sub"
    }, [domainHost, created ? `analyzed ${created === "Today" || created === "Yesterday" ? created.toLowerCase() : created}` : null].filter(Boolean).join(" · "))), /*#__PURE__*/React.createElement("div", {
      className: "apr-actions"
    }, arrLocations.length > 0 ? /*#__PURE__*/React.createElement(Popover, {
      color: "secondary",
      label: arrLocations.length === 1 ? "1 location" : `${arrLocations.length} locations`,
      icon: "arrow_drop_down"
    }, /*#__PURE__*/React.createElement("div", {
      className: "dl-locations"
    }, arrLocations.map((l, i) => /*#__PURE__*/React.createElement("div", {
      key: i
    }, l)))) : null, /*#__PURE__*/React.createElement(Button, {
      onClick: this.onExportClick
    }, "Export"), /*#__PURE__*/React.createElement(Button, {
      onClick: this.rerun,
      disabled: rerunning,
      title: "Run a fresh analysis of this company"
    }, rerunning ? "Starting…" : "Run again"))), notice && /*#__PURE__*/React.createElement("div", {
      className: "apr-notice"
    }, notice, " ", /*#__PURE__*/React.createElement("a", {
      onClick: () => this.setState({
        notice: null
      })
    }, "Dismiss")), tiles.length ? /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      tiles: tiles,
      cols: tiles.length === 3 ? 3 : undefined
    }) : null, /*#__PURE__*/React.createElement("div", {
      className: competitorsCard ? "apr-two" : "apr-one"
    }, competitorsCard, /*#__PURE__*/React.createElement("div", {
      className: "apr-sections"
    }, shown.length ? shown.map((s, i) => /*#__PURE__*/React.createElement("section", {
      className: "apr-card apr-text",
      key: i
    }, /*#__PURE__*/React.createElement("b", null, s.label), /*#__PURE__*/React.createElement(Md, {
      source: s.body
    }))) : /*#__PURE__*/React.createElement("section", {
      className: "apr-card apr-text"
    }, /*#__PURE__*/React.createElement("span", {
      className: "apr-muted"
    }, "This report has no written sections.")))), hidden.length ? /*#__PURE__*/React.createElement("p", {
      className: "apr-hidden-note"
    }, "Sections with nothing found (", hidden.join(", "), ") are hidden.") : null);
  }
}
export class CompanyDetailViewComponent extends BaseDataViewComponentAsync {
  constructor(props) {
    super(props);
  }
  async fetchData() {
    var Service = ServiceFactory.getType('/company-analysis/' + this.props.params.id, 'research');
    var service = new Service();
    try {
      var data = await service.getAsync();
      this.allowRender = true;
      this.data = data;
      this.allowUpdate = false;
      this.forceUpdate();
    } catch (err) {
      this.error = true;
      this.errorMessage = err.message;
      this.allowRender = true;
      this.allowUpdate = false;
      this.forceUpdate();
    }
  }
  render() {
    if (!this.allowRender) {
      return /*#__PURE__*/React.createElement(Loader, null);
    } else if (this.error) {
      return /*#__PURE__*/React.createElement(Error, {
        message: this.errorMessage
      });
    } else {
      return /*#__PURE__*/React.createElement(RoutableComponent, {
        data: this.data,
        params: this.props.params
      });
    }
  }
}
const RoutableComponent = withRouter(MainComponent);
export default {
  CompanyDetailViewComponent
};